import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { Step } from '@/types/database';

export type AssistantMode = 'general' | 'step' | 'material' | 'suggest';

// Context level for general assistant - determines workflow priority
export type GeneralAssistantContextLevel =
  | 'dashboard'
  | 'help-center'
  | 'project'
  | 'project-step-expanded'
  | 'project-substep-expanded'
  | 'settings'
  | 'activity'
  | 'other';

export interface StepContext {
  step: Step;
  parentStepTitle?: string;
  projectId?: string;
  projectTitle?: string;
  projectSummary?: string;
  projectBudget?: number;
  projectStatus?: string;
  totalSteps?: number;
  completedSteps?: number;
  totalSpent?: number;
  existingSubSteps?: Array<{
    id: string;
    title: string;
    summary?: string | null;
    amount_spent: number;
    expected_cost?: number;
  }>;
  existingMaterials?: Array<{
    id: string;
    title: string;
    quantity?: number;
    estimated_cost?: number;
    is_purchased?: boolean;
  }>;
  onCreateSubStep?: (data: {
    title: string;
    summary?: string;
    amount_spent?: number;
    expected_cost?: number;
    materials?: Array<{ title: string; quantity?: number; estimated_cost?: number }>;
  }) => Promise<{ id: string } | void>;
  onDeleteSubStep?: (stepId: string) => Promise<void>;
  onUpdateStep?: (data: { title: string; summary?: string; amount_spent: number }) => Promise<void>;
  onUpdateSubStep?: (stepId: string, data: { title?: string; summary?: string; amount_spent?: number }) => Promise<void>;
  initialQuery?: string;
  hiddenInstructions?: string;
  guideMode?: boolean;
}

export interface MaterialContext {
  stepId: string;
  stepTitle: string;
  projectId?: string;
  projectTitle?: string;
  initialQuery?: string;
}

export interface SuggestStepMaterial {
  title: string;
  quantity?: number;
  estimated_cost?: number;
}

export interface SuggestContext {
  projectId: string;
  projectTitle: string;
  projectSummary?: string | null;
  onCreateSteps?: (steps: { title: string; summary: string; expected_cost: number; materials?: SuggestStepMaterial[]; weather_sensitivity?: string }[]) => Promise<void>;
}

export interface GeneralAssistantLaunchContext {
  level: GeneralAssistantContextLevel;
  projectId?: string;
  projectTitle?: string;
  expandedStepId?: string;
  expandedStepTitle?: string;
  expandedSubStepId?: string;
  expandedSubStepTitle?: string;
  pagePath: string;
  initialQuery?: string;
  initialImages?: { preview: string; base64: string }[];
  hiddenInstructions?: string;
  smartNotificationContext?: {
    triggerType: 'due_soon' | 'off_track' | 'materials_unpurchased';
    reason: string;
    suggestions: string[];
    dueDate?: string;
    daysUntilDue?: number;
    progressPercentage?: number;
    unpurchasedMaterials?: number;
    aiAnalysis?: string;
  };
}

// ─── Split Contexts ────────────────────────────────────────────────────────────

/** Visibility state — changes on every open/close/mode switch */
interface AIAssistantVisibilityValue {
  isOpen: boolean;
  mode: AssistantMode;
}

/** Context payloads — changes when the user opens a different entity */
interface AIAssistantPayloadValue {
  stepContext: StepContext | null;
  materialContext: MaterialContext | null;
  suggestContext: SuggestContext | null;
  generalLaunchContext: GeneralAssistantLaunchContext | null;
}

/** Settings — changes rarely */
interface AIAssistantSettingsValue {
  masterAIOverride: boolean;
  setMasterAIOverride: (enabled: boolean) => void;
}

/** Actions — stable references, never cause re-renders */
interface AIAssistantActionsValue {
  openAssistant: (mode?: AssistantMode) => void;
  openGeneralAssistant: (launchContext: GeneralAssistantLaunchContext) => void;
  openStepAssistant: (context: StepContext) => void;
  openMaterialAssistant: (context: MaterialContext) => void;
  openSuggestAssistant: (context: SuggestContext) => void;
  closeAssistant: () => void;
  setMode: (mode: AssistantMode) => void;
  clearContext: () => void;
  updateGeneralLaunchContext: (launchContext: GeneralAssistantLaunchContext) => void;
}

const AIAssistantVisibilityContext = createContext<AIAssistantVisibilityValue | null>(null);
const AIAssistantPayloadContext = createContext<AIAssistantPayloadValue | null>(null);
const AIAssistantSettingsContext = createContext<AIAssistantSettingsValue | null>(null);
const AIAssistantActionsContext = createContext<AIAssistantActionsValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AIAssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setModeState] = useState<AssistantMode>('general');
  const [stepContext, setStepContext] = useState<StepContext | null>(null);
  const [materialContext, setMaterialContext] = useState<MaterialContext | null>(null);
  const [suggestContext, setSuggestContext] = useState<SuggestContext | null>(null);
  const [generalLaunchContext, setGeneralLaunchContext] = useState<GeneralAssistantLaunchContext | null>(null);
  const [masterAIOverride, setMasterAIOverride] = useState(false);

  // Ref keeps actions stable — no re-creation when masterAIOverride changes
  const masterAIOverrideRef = useRef(masterAIOverride);
  useEffect(() => { masterAIOverrideRef.current = masterAIOverride; }, [masterAIOverride]);

  const openAssistant = useCallback((newMode: AssistantMode = 'general') => {
    setModeState(newMode);
    setIsOpen(true);
  }, []);

  const openGeneralAssistant = useCallback((launchContext: GeneralAssistantLaunchContext) => {
    if (masterAIOverrideRef.current) {
      const query = launchContext.initialQuery ||
        (launchContext.expandedStepTitle
          ? `Help me with step "${launchContext.expandedStepTitle}" in project "${launchContext.projectTitle || ''}"`
          : launchContext.projectTitle
            ? `Help me with project "${launchContext.projectTitle}"`
            : 'How can you help me?');
      window.dispatchEvent(new CustomEvent('master-ai-context-open', { detail: { contextQuery: query } }));
      return;
    }
    setGeneralLaunchContext(launchContext);
    setStepContext(null);
    setMaterialContext(null);
    setSuggestContext(null);
    setModeState('general');
    setIsOpen(true);
  }, []);

  const openStepAssistant = useCallback((context: StepContext) => {
    if (masterAIOverrideRef.current) {
      const query = context.initialQuery || `Help me with step "${context.step.title}" in project "${context.projectTitle || ''}"`;
      window.dispatchEvent(new CustomEvent('master-ai-context-open', { detail: { contextQuery: query, stepId: context.step.id } }));
      return;
    }
    setStepContext(context);
    setMaterialContext(null);
    setSuggestContext(null);
    setGeneralLaunchContext(null);
    setModeState('step');
    setIsOpen(true);
  }, []);

  const openMaterialAssistant = useCallback((context: MaterialContext) => {
    if (masterAIOverrideRef.current) {
      const query = context.initialQuery || `Help me find materials for step "${context.stepTitle}"`;
      window.dispatchEvent(new CustomEvent('master-ai-context-open', { detail: { contextQuery: query } }));
      return;
    }
    setMaterialContext(context);
    setStepContext(null);
    setSuggestContext(null);
    setGeneralLaunchContext(null);
    setModeState('material');
    setIsOpen(true);
  }, []);

  const openSuggestAssistant = useCallback((context: SuggestContext) => {
    if (masterAIOverrideRef.current) {
      const query = `Suggest steps for project "${context.projectTitle}"`;
      window.dispatchEvent(new CustomEvent('master-ai-context-open', { detail: { contextQuery: query } }));
      return;
    }
    setSuggestContext(context);
    setStepContext(null);
    setMaterialContext(null);
    setGeneralLaunchContext(null);
    setModeState('suggest');
    setIsOpen(true);
  }, []);

  const closeAssistant = useCallback(() => { setIsOpen(false); }, []);

  const clearContext = useCallback(() => {
    setStepContext(null);
    setMaterialContext(null);
    setSuggestContext(null);
    setGeneralLaunchContext(null);
    setModeState('general');
  }, []);

  const updateGeneralLaunchContext = useCallback((launchContext: GeneralAssistantLaunchContext) => {
    setGeneralLaunchContext(launchContext);
  }, []);

  const setMode = useCallback((newMode: AssistantMode) => {
    setModeState(newMode);
  }, []);

  return (
    <AIAssistantVisibilityContext.Provider value={{ isOpen, mode }}>
      <AIAssistantPayloadContext.Provider value={{ stepContext, materialContext, suggestContext, generalLaunchContext }}>
        <AIAssistantSettingsContext.Provider value={{ masterAIOverride, setMasterAIOverride }}>
          <AIAssistantActionsContext.Provider value={{
            openAssistant,
            openGeneralAssistant,
            openStepAssistant,
            openMaterialAssistant,
            openSuggestAssistant,
            closeAssistant,
            setMode,
            clearContext,
            updateGeneralLaunchContext,
          }}>
            {children}
          </AIAssistantActionsContext.Provider>
        </AIAssistantSettingsContext.Provider>
      </AIAssistantPayloadContext.Provider>
    </AIAssistantVisibilityContext.Provider>
  );
}

// ─── Focused hooks (use these in new components for precision) ─────────────────

export function useAIAssistantVisibility() {
  const ctx = useContext(AIAssistantVisibilityContext);
  if (!ctx) throw new Error('useAIAssistantVisibility must be used within AIAssistantProvider');
  return ctx;
}

export function useAIAssistantPayload() {
  const ctx = useContext(AIAssistantPayloadContext);
  if (!ctx) throw new Error('useAIAssistantPayload must be used within AIAssistantProvider');
  return ctx;
}

export function useAIAssistantSettings() {
  const ctx = useContext(AIAssistantSettingsContext);
  if (!ctx) throw new Error('useAIAssistantSettings must be used within AIAssistantProvider');
  return ctx;
}

export function useAIAssistantActions() {
  const ctx = useContext(AIAssistantActionsContext);
  if (!ctx) throw new Error('useAIAssistantActions must be used within AIAssistantProvider');
  return ctx;
}

// ─── Backward-compatible combined hook ────────────────────────────────────────
// Existing components that call useAIAssistant() continue to work unchanged.
// New components should prefer the focused hooks above.

export function useAIAssistant() {
  return {
    ...useAIAssistantVisibility(),
    ...useAIAssistantPayload(),
    ...useAIAssistantSettings(),
    ...useAIAssistantActions(),
  };
}
